import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { Eye, EyeOff, Check, ArrowRight } from 'lucide-react';
import { GoogleAuthButton } from './GoogleAuthButton';
import { cn } from '@/lib/utils';

const PLAN_STORAGE_KEY = 'sapling-selected-plan';

const PLAN_DETAILS: Record<string, { name: string; price: string }> = {
  solo: { name: 'Solo', price: '$10/mo' },
  pro: { name: 'Pro', price: '$25/mo' },
  team: { name: 'Team', price: '$50/mo' },
};

interface SignupFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
  onChangePlan?: () => void;
}

export function SignupForm({
  onSuccess,
  onSwitchToLogin,
  onChangePlan,
}: SignupFormProps) {
  const { signUp, loading } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    fullName?: string;
    email?: string;
    password?: string;
  }>({});

  // Load selected plan from sessionStorage
  useEffect(() => {
    const storedPlan = sessionStorage.getItem(PLAN_STORAGE_KEY);
    if (storedPlan && ['solo', 'pro', 'team'].includes(storedPlan)) {
      setSelectedPlan(storedPlan);
    }
  }, []);

  // Password strength indicators
  const passwordChecks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;

  const validateForm = () => {
    const newErrors: { fullName?: string; email?: string; password?: string } =
      {};

    if (!fullName.trim()) {
      newErrors.fullName = 'Name is required';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const result = await signUp(email, password, fullName);

    if (result.success) {
      if (result.error) {
        // Email confirmation required
        toast({
          title: 'Check your email',
          description: result.error,
          variant: 'success',
        });
      }
      onSuccess?.();
    } else {
      toast({
        title: 'Signup failed',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Selected plan banner */}
      {selectedPlan && PLAN_DETAILS[selectedPlan] && (
        <div className="flex items-center justify-between rounded-btn border border-sapling-primary-100/30 bg-sapling-primary-100/5 p-3">
          <div>
            <span className="text-sm text-muted-foreground">
              Selected plan:{' '}
            </span>
            <span className="font-semibold">
              {PLAN_DETAILS[selectedPlan].name}
            </span>
            <span className="text-muted-foreground">
              {' '}
              ({PLAN_DETAILS[selectedPlan].price})
            </span>
          </div>
          <button
            type="button"
            onClick={onChangePlan}
            className="text-sm text-sapling-primary-200 hover:text-sapling-primary-100 dark:text-sapling-primary-100"
          >
            change
          </button>
        </div>
      )}

      {/* Full Name */}
      <div className="space-y-2">
        <Label htmlFor="signup-name">Full Name</Label>
        <Input
          id="signup-name"
          type="text"
          placeholder="John Smith"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={!!errors.fullName}
          disabled={loading}
          autoComplete="name"
        />
        {errors.fullName && (
          <p className="text-sm text-sapling-error-100">{errors.fullName}</p>
        )}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!errors.email}
          disabled={loading}
          autoComplete="email"
        />
        {errors.email && (
          <p className="text-sm text-sapling-error-100">{errors.email}</p>
        )}
      </div>

      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <div className="relative">
          <Input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            disabled={loading}
            autoComplete="new-password"
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="text-sm text-sapling-error-100">{errors.password}</p>
        )}

        {/* Password strength indicator */}
        {password && (
          <div className="space-y-2">
            <div className="flex gap-1">
              {[1, 2, 3, 4].map((level) => (
                <div
                  key={level}
                  className={cn(
                    'h-1 flex-1 rounded-full transition-colors',
                    passwordStrength >= level
                      ? passwordStrength === 4
                        ? 'bg-sapling-primary-100'
                        : passwordStrength >= 3
                          ? 'bg-sapling-warning-100'
                          : 'bg-sapling-error-100'
                      : 'bg-muted'
                  )}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <PasswordCheck checked={passwordChecks.length} label="8+ characters" />
              <PasswordCheck checked={passwordChecks.lowercase} label="Lowercase" />
              <PasswordCheck checked={passwordChecks.uppercase} label="Uppercase" />
              <PasswordCheck checked={passwordChecks.number} label="Number" />
            </div>
          </div>
        )}
      </div>

      {/* Submit button */}
      <Button type="submit" className="w-full" loading={loading}>
        {selectedPlan ? (
          <>
            Create Account & Continue to Payment
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        ) : (
          'Create Account'
        )}
      </Button>

      {/* Terms */}
      <p className="text-center text-xs text-muted-foreground">
        By signing up, you agree to our{' '}
        <a
          href="https://oasisautokonnect.com/pages/terms.html"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://oasisautokonnect.com/pages/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          Privacy Policy
        </a>
      </p>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      {/* Social signup */}
      <GoogleAuthButton />

      {/* Switch to login */}
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-sapling-primary-200 hover:text-sapling-primary-100 dark:text-sapling-primary-100 font-medium"
        >
          Log in
        </button>
      </p>
    </form>
  );
}

function PasswordCheck({
  checked,
  label,
}: {
  checked: boolean;
  label: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1',
        checked ? 'text-sapling-primary-200' : 'text-muted-foreground'
      )}
    >
      <Check className={cn('h-3 w-3', !checked && 'opacity-0')} />
      <span>{label}</span>
    </div>
  );
}
